import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import InputChip from 'LuxKit/Chips/InputChip'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import ExperienceDenseSearchList from 'components/Experiences/ExperienceLists/ExperienceDenseSearchList'
import useActiveExperienceFilterChips from 'components/Experiences/hooks/useActiveExperienceFilterChips'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import ContactContext from 'contexts/contactContext'

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

interface Props {
  placeId: string;
  destinationName: string;
}

function ExperienceSearchNoResults({ placeId, destinationName }: Props) {
  const intl = useIntl()
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const contactCtx = useContext(ContactContext)
  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Popular Experiences No Results'),
    { key: placeId },
  )

  const onAfterDateRemove = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: DATE_SEARCH_OPTION_IDS.ANYTIME,
    })
  }, [searchDispatch])

  const chips = useActiveExperienceFilterChips(onAfterDateRemove)

  return (
    <VerticalSpacer gap={16}>
      <VerticalSpacer gap={8}>
        <Heading variant="heading3">
          {intl.formatMessage({
            id: '50pio0W0931H_t9eX1Y5',
            defaultMessage: "Sorry, we couldn't find any experiences",
            description:
              'Heading shown when no experiences match the search filters on the list page',
          })}
        </Heading>
        <BodyText variant="large">
          <FormattedMessage
            id="UYksxIGc9TiwX-T-yWqv"
            defaultMessage="Couldn't find what you're looking for? Speak to one of our travel experts <link>{phone}</link> or adjust your filters:"
            description="Body text with support phone number when no experiences match the search filters"
            values={{
              link: (chunks: React.ReactNode) => (
                <TextLink
                  href={`tel:${contactCtx.defaultContact.number}`}
                  rel="nofollow"
                >
                  {chunks}
                </TextLink>
              ),
              phone: contactCtx.defaultContact.humanReadable,
            }}
          />
        </BodyText>
      </VerticalSpacer>

      {chips.length > 0 && (
        <ChipsContainer>
          {chips.map((chip) => (
            <InputChip key={chip.key} variant="filled" onClick={chip.onRemove}>
              {chip.label}
            </InputChip>
          ))}
        </ChipsContainer>
      )}

      <VerticalSpacer gap={16}>
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <Heading variant="heading4">
            {intl.formatMessage(
              {
                id: 'Hg0nQo391PO-iYUYLBPr',
                defaultMessage: 'Popular experiences in {destination}',
                description:
                  'Heading for the popular experiences section shown when no search results are found',
              },
              { destination: destinationName },
            )}
          </Heading>
          <CSSBreakpoint min="tablet">
            <ExperienceSearchPageSort />
          </CSSBreakpoint>
        </Group>
        <OfferListEventsProvider tracking={tracking}>
          <ExperienceDenseSearchList placeId={placeId} />
        </OfferListEventsProvider>
      </VerticalSpacer>
    </VerticalSpacer>
  )
}

export default ExperienceSearchNoResults
